import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { SkillsLibraryTab } from './SkillsLibraryTab';
import { SkillsSystemTab } from './SkillsSystemTab';
import { SkillEditor } from './SkillEditor';
import {
  ADP_EVENTS,
  getBernoulliAdp,
  trackAdpEvent,
} from '@/services/analytics/adp';

type Tab = 'my' | 'system';

type DetailState =
  | { kind: 'edit'; skillId: string; readOnly?: boolean }
  | { kind: 'create'; scope: 'user' | 'tenant' }
  | null;

export const SkillsPage = ({ onClose }: { onClose: () => void }) => {
  const [tab, setTab] = useState<Tab>('my');
  const [refreshKey, setRefreshKey] = useState(0);
  const [detail, setDetail] = useState<DetailState>(null);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (detail) {
          setDetail(null);
        } else {
          onClose();
        }
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose, detail]);

  const tabs: { id: Tab; label: string }[] = [
    { id: 'my', label: 'My skills' },
    { id: 'system', label: 'Default skills' },
  ];

  const bumpRefresh = () => setRefreshKey(k => k + 1);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="skills-dialog-title"
      className="fixed inset-0 z-40">
      <button
        type="button"
        aria-label="Close skills"
        onClick={onClose}
        className="absolute inset-0 bg-black/40 cursor-default"
      />
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none p-4 sm:p-6 md:p-10">
        <div className="bg-white rounded-xl shadow-2xl flex flex-col w-full max-w-3xl h-full max-h-[80vh] pointer-events-auto overflow-hidden">
          <div className="flex-shrink-0 border-b border-slate-200/80 bg-white">
            <div className="flex items-center justify-between px-6 py-4">
              <h1
                id="skills-dialog-title"
                className="text-lg font-semibold text-[#252F33]">
                Skills
              </h1>
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 rounded-md hover:bg-slate-100 text-[#252F33]"
                aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>
            {!detail && (
              <div className="flex gap-1 px-6">
                {tabs.map(t => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => {
                      trackAdpEvent(
                        getBernoulliAdp(),
                        ADP_EVENTS.SKILLS_TAB_SWITCH,
                        {
                          tab: t.id,
                        },
                      );
                      setTab(t.id);
                    }}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      tab === t.id
                        ? 'border-brand-dark text-brand-dark'
                        : 'border-transparent text-[#768D95] hover:text-[#252F33]'
                    }`}>
                    {t.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-6 bg-[#FAFBFC]">
            {detail?.kind === 'edit' ? (
              <SkillEditor
                skillId={detail.skillId}
                readOnly={detail.readOnly}
                onBack={() => setDetail(null)}
                onSaved={bumpRefresh}
                onDeleted={() => {
                  bumpRefresh();
                  setDetail(null);
                }}
              />
            ) : detail?.kind === 'create' ? (
              <SkillEditor
                createScope={detail.scope}
                onBack={() => setDetail(null)}
                onSaved={bumpRefresh}
              />
            ) : tab === 'my' ? (
              <SkillsLibraryTab
                refreshKey={refreshKey}
                onOpenSkill={(id, _scope, readOnly) =>
                  setDetail({ kind: 'edit', skillId: id, readOnly })
                }
                onCreateNew={scope => setDetail({ kind: 'create', scope })}
              />
            ) : (
              <SkillsSystemTab refreshKey={refreshKey} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
